import React, { useEffect, useLayoutEffect, useState } from 'react';
import { HBCountryFlagProps } from './HBCountryFlag.types';
import {
    FLAG_FONT_FAMILY,
    codeToFlagEmoji,
    ensureFlagFont,
    flagEmojiSupported
} from './flagEmojiSupport';

// useLayoutEffect warns when rendered on the server; useEffect is a no-op there.
const useIsomorphicLayoutEffect =
    typeof window !== 'undefined' ? useLayoutEffect : useEffect;

/**
 * Renders a country flag by ISO 3166-1 alpha-2 code.
 *
 * On platforms that render flag emoji natively (Apple, Noto, Firefox) this is
 * just the native emoji character — zero extra bytes, and the real wavy Apple
 * flags on macOS/iOS. On platforms that don't (Windows + Chrome/Edge, where a
 * flag degrades to its two-letter code), it lazily loads a flags-only webfont
 * for that cohort only. See flagEmojiSupport.ts for the full rationale.
 *
 * While that font loads, the browser shows the two-letter code. When the flag is
 * decorative (`aria-hidden`) an adjacent label already names the country, so we
 * hide the glyph until the flag is ready rather than flashing e.g. "AU". When the
 * flag stands alone the code is meaningful, so we let it show during loading.
 */
const HBCountryFlag = ({
    country,
    title,
    'aria-hidden': ariaHidden,
    size,
    className,
    testId
}: HBCountryFlagProps) => {
    // Start from what the server renders (native emoji) so hydration matches, then
    // detect in a layout effect, which re-renders before the first paint, so
    // client-only renders never flash the native two-letter code either.
    const [supported, setSupported] = useState(true);
    const [fontReady, setFontReady] = useState(false);

    useIsomorphicLayoutEffect(() => {
        if (flagEmojiSupported()) {
            return;
        }
        setSupported(false);
        let cancelled = false;
        // Resolves when the font is paint-ready, or on failure (so a hidden flag
        // still reveals — falling back to the two-letter code — rather than
        // staying blank forever).
        void ensureFlagFont().then(() => {
            if (!cancelled) {
                setFontReady(true);
            }
        });
        return () => {
            cancelled = true;
        };
    }, []);

    const flag = codeToFlagEmoji(country);

    // Decorative flags hide until the webfont is ready; standalone flags keep the
    // two-letter code visible meanwhile (see the component doc comment).
    const hideUntilFontReady = !supported && !!ariaHidden && !fontReady;

    const sizeCss =
        size === undefined
            ? undefined
            : typeof size === 'number'
            ? `${size}px`
            : size;

    // At a given font-size Twemoji flags render ~10% larger than native Apple
    // flags (measured on the ink, not the advance), so scale the fallback down to
    // match the native experience. Native rendering is left untouched.
    const glyphSize = supported ? sizeCss : `calc(${sizeCss ?? '1em'} * 0.9)`;

    // Reserve a fixed 4:3 box (flag aspect) so the glyph swap never shifts layout.
    const reserveBox = sizeCss !== undefined || (!supported && !!ariaHidden);
    const boxHeight = glyphSize ?? '1em';

    const style: React.CSSProperties = {};
    if (!supported) {
        // Scoped flags-only webfont; generic fallback for any non-flag content.
        style.fontFamily = `${FLAG_FONT_FAMILY}, sans-serif`;
    }
    if (hideUntilFontReady) {
        style.visibility = 'hidden';
    }
    if (glyphSize !== undefined) {
        style.fontSize = glyphSize;
    }
    if (reserveBox) {
        style.display = 'inline-block';
        style.width = `calc(${boxHeight} * 4 / 3)`;
        style.height = boxHeight;
        style.lineHeight = boxHeight;
        style.textAlign = 'center';
        style.verticalAlign = 'middle';
    }

    return (
        <span
            className={className}
            style={style}
            role={ariaHidden ? undefined : 'img'}
            aria-hidden={ariaHidden || undefined}
            aria-label={ariaHidden ? undefined : title ?? country}
            // Decorative flags sit next to a visible label; a native tooltip would
            // only repeat it.
            title={ariaHidden ? undefined : title ?? country}
            data-testid={testId}
        >
            {flag}
        </span>
    );
};

export default HBCountryFlag;
